import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

import type { SearchEntityVersionFragment } from 'types/graphql'

import useShowConditionsUi from 'src/hooks/use-show-conditions-ui'
import { conditionEntityLabel } from 'src/utils/condition-entity-display-prefix'

type ApplyConditionsCallbacksContextType = {
  onEntityVersionsChange: (entityVersions: SearchEntityVersionFragment[], fieldKey: string) => void
}

const ApplyConditionsCallbacksContext = createContext<ApplyConditionsCallbacksContextType>({
  onEntityVersionsChange: () => {},
})

const mergeEntityVersionsByField = (
  entityVersionsByField: ReadonlyMap<string, readonly SearchEntityVersionFragment[]>
): SearchEntityVersionFragment[] => {
  const byVersionId = new Map<number, SearchEntityVersionFragment>()
  for (const entityVersions of entityVersionsByField.values()) {
    for (const entityVersion of entityVersions) {
      byVersionId.set(entityVersion.id, entityVersion)
    }
  }
  return [...byVersionId.values()]
}

type ConditionFromEntityVersion = NonNullable<
  NonNullable<SearchEntityVersionFragment['entity']>['activeConditions']
>[number]

type ApplyConditionsStateContextType = {
  activeModalEntityId: number | null
  bodyHighlightText: string
  conditions: readonly ConditionFromEntityVersion[]
  headingText: string
  shouldShowModal: boolean
  onAcknowledge: () => void
}

const ApplyConditionsStateContext = createContext<ApplyConditionsStateContextType>({
  activeModalEntityId: null,
  bodyHighlightText: '',
  conditions: [],
  headingText: '',
  shouldShowModal: false,
  onAcknowledge: () => {},
})

type ProviderProps = {
  children: React.ReactNode
}

export const ApplyConditionsProvider = ({ children }: ProviderProps) => {
  const showConditionsUi = useShowConditionsUi()
  const [selectedEntityVersions, setSelectedEntityVersions] = useState<
    SearchEntityVersionFragment[]
  >([])
  const [activeModalEntityId, setActiveModalEntityId] = useState<number | null>(null)

  const priorEntityVersionIdsByFieldRef = useRef<Map<string, ReadonlySet<number>>>(new Map())
  const entityVersionsByFieldRef = useRef<Map<string, SearchEntityVersionFragment[]>>(new Map())

  const onEntityVersionsChange = useCallback(
    (entityVersions: SearchEntityVersionFragment[], fieldKey: string) => {
      if (!showConditionsUi) {
        return
      }

      const priorEntityVersionIds =
        priorEntityVersionIdsByFieldRef.current.get(fieldKey) ?? new Set<number>()

      priorEntityVersionIdsByFieldRef.current.set(
        fieldKey,
        new Set(entityVersions.map((v) => v.id))
      )
      entityVersionsByFieldRef.current.set(fieldKey, entityVersions)

      const mergedEntityVersions = mergeEntityVersionsByField(entityVersionsByFieldRef.current)
      setSelectedEntityVersions(mergedEntityVersions)

      setActiveModalEntityId((previousActiveEntityId) => {
        const newlyAddedEntityVersions = entityVersions.filter(
          (v) => !priorEntityVersionIds.has(v.id)
        )

        if (newlyAddedEntityVersions.length > 0) {
          const pick = newlyAddedEntityVersions.find((v) => v.entity?.activeConditions?.length)
          if (pick?.entity?.id != null) {
            return pick.entity.id
          }
        }

        const stillSelected =
          previousActiveEntityId != null &&
          mergedEntityVersions.some((v) => v.entity?.id === previousActiveEntityId)
        const rowForPrevious =
          previousActiveEntityId != null
            ? mergedEntityVersions.find((v) => v.entity?.id === previousActiveEntityId)
            : undefined
        const stillHasConditions = !!rowForPrevious?.entity?.activeConditions?.length

        if (stillSelected && stillHasConditions && previousActiveEntityId != null) {
          return previousActiveEntityId
        }

        return null
      })
    },
    [showConditionsUi]
  )

  const onAcknowledge = useCallback(() => {
    setActiveModalEntityId(null)
  }, [])

  const callbacksValue = useMemo(
    () => ({
      onEntityVersionsChange,
    }),
    [onEntityVersionsChange]
  )

  const { bodyHighlightText, conditions, headingText } = useMemo(() => {
    if (activeModalEntityId == null) {
      return {
        bodyHighlightText: '',
        conditions: [] as ConditionFromEntityVersion[],
        headingText: '',
      }
    }
    const row = selectedEntityVersions.find((v) => v.entity?.id === activeModalEntityId)
    const entity = row?.entity
    if (!entity) {
      return {
        bodyHighlightText: '',
        conditions: [] as ConditionFromEntityVersion[],
        headingText: '',
      }
    }
    const label = conditionEntityLabel(entity.name ?? 'Property', entity.layer?.type ?? 'Address')
    return {
      bodyHighlightText: label,
      conditions: entity.activeConditions ?? [],
      headingText: `${label} has conditions`,
    }
  }, [activeModalEntityId, selectedEntityVersions])

  const shouldShowModal = conditions.length > 0 && activeModalEntityId != null

  const stateValue = useMemo(
    () => ({
      activeModalEntityId,
      bodyHighlightText,
      conditions,
      headingText,
      shouldShowModal,
      onAcknowledge,
    }),
    [
      activeModalEntityId,
      bodyHighlightText,
      conditions,
      headingText,
      shouldShowModal,
      onAcknowledge,
    ]
  )

  return (
    <ApplyConditionsCallbacksContext.Provider value={callbacksValue}>
      <ApplyConditionsStateContext.Provider value={stateValue}>
        {children}
      </ApplyConditionsStateContext.Provider>
    </ApplyConditionsCallbacksContext.Provider>
  )
}

export const useApplyConditionsSelection = () => {
  return useContext(ApplyConditionsCallbacksContext)
}

export const useApplyConditionsState = () => {
  return useContext(ApplyConditionsStateContext)
}
